import { useState } from 'react';
import type { ReadinessIssue } from './store';
import { Btn, Icon, ICONS } from '../ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../ui/tokens';
import { hostedFormUrl, embedSnippet } from '../hostedUrl';

const STANDARD_CHECKS = [
  'At least one field added',
  'All field keys are unique',
  'Path-type requirements met (email on contact, ≥1 updateable on candidate)',
  'Conditional logic references resolve',
] as const;

export function PublishModal(props: {
  issues: ReadinessIssue[];
  isPublishing: boolean;
  nextVersionNumber: number;
  slug: string;
  onClose: () => void;
  onPublish: () => void | Promise<void>;
}) {
  const ready = props.issues.length === 0;
  // Once publish resolves we flip to a confirmation view (hosted URL + embed
  // snippet) instead of silently closing — this is the "Form saved" prompt.
  const [published, setPublished] = useState(false);

  const handlePublish = async () => {
    await props.onPublish();
    setPublished(true);
  };

  if (published) {
    return (
      <PublishSuccess
        slug={props.slug}
        versionNumber={props.nextVersionNumber}
        onClose={props.onClose}
      />
    );
  }

  return (
    <div
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,24,60,.32)',
        backdropFilter: 'blur(1px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '60px 24px',
        zIndex: 200,
        fontFamily: FORM_UI_FONT,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,18,60,.28)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '22px 24px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h2 style={{ fontSize: 19, fontWeight: 800, color: C.indigo, margin: 0 }}>
              Publish form
            </h2>
            <div style={{ fontSize: 12.5, color: C.sub, marginTop: 3 }}>
              Publishing creates <b>version {props.nextVersionNumber}</b>. New submissions
              reference v{props.nextVersionNumber}; existing links keep working.
            </div>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: C.faint,
            }}
            aria-label="Close"
          >
            <Icon d={ICONS.x} size={20} color={C.faint} />
          </button>
        </div>

        <div style={{ padding: '0 24px 8px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.faint,
              letterSpacing: 0.4,
              marginBottom: 10,
            }}
          >
            READINESS CHECKLIST
          </div>
          {STANDARD_CHECKS.map((label, idx) => (
            <ChecklistRow
              key={label}
              label={label}
              pass
              isLast={idx === STANDARD_CHECKS.length - 1 && props.issues.length === 0}
            />
          ))}
          {props.issues.map((issue, idx) => (
            <ChecklistRow
              key={`fail-${idx}`}
              label={issue.message}
              pass={false}
              isLast={idx === props.issues.length - 1}
            />
          ))}
        </div>

        <div
          style={{
            padding: '16px 24px 22px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <Btn kind="ghost" onClick={props.onClose}>
            Cancel
          </Btn>
          <Btn
            kind="primary"
            disabled={!ready || props.isPublishing}
            onClick={handlePublish}
          >
            {props.isPublishing ? 'Publishing…' : `Publish v${props.nextVersionNumber}`}
          </Btn>
        </div>

        {!ready && (
          <div
            style={{
              background: C.dangerBg,
              color: C.danger,
              fontSize: 11.5,
              fontWeight: 600,
              textAlign: 'center',
              padding: '9px 0',
            }}
          >
            Resolve {props.issues.length} failing {props.issues.length === 1 ? 'check' : 'checks'}{' '}
            to enable publish
          </div>
        )}
      </div>
    </div>
  );
}

// ─── success / confirmation view ──────────────────────────────────────────────

function PublishSuccess(props: { slug: string; versionNumber: number; onClose: () => void }) {
  const hostedUrl = hostedFormUrl(props.slug);
  const embed = embedSnippet(props.slug);

  return (
    <div
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,24,60,.32)',
        backdropFilter: 'blur(1px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '60px 24px',
        zIndex: 200,
        fontFamily: FORM_UI_FONT,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,18,60,.28)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '26px 24px 8px', textAlign: 'center' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 46,
              height: 46,
              borderRadius: 99,
              background: C.contactBg,
              marginBottom: 12,
            }}
          >
            <Icon d={ICONS.check} size={22} color={C.tealDark} sw={2.6} />
          </span>
          <h2 style={{ fontSize: 19, fontWeight: 800, color: C.indigo, margin: 0 }}>
            Form published 🎉
          </h2>
          <div style={{ fontSize: 12.5, color: C.sub, marginTop: 4 }}>
            Version {props.versionNumber} is now live. Share the link below or embed it on any website.
          </div>
        </div>

        <div style={{ padding: '16px 24px 6px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <CopyField label="Hosted form link" value={hostedUrl} mono />
          <CopyField label="Embed script (paste into your website's HTML)" value={embed} mono multiline />
        </div>

        <div
          style={{
            padding: '12px 24px 22px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <Btn kind="ghost" onClick={() => window.open(hostedUrl, '_blank')}>
            <Icon d={ICONS.eye} size={15} color={C.ink} />
            Open form
          </Btn>
          <Btn kind="primary" onClick={props.onClose}>
            Done
          </Btn>
        </div>
      </div>
    </div>
  );
}

function CopyField(props: { label: string; value: string; mono?: boolean; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — the value is still selectable in the field */
    }
  };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, letterSpacing: 0.4, marginBottom: 6 }}>
        {props.label.toUpperCase()}
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
        <textarea
          readOnly
          value={props.value}
          rows={props.multiline ? 2 : 1}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            resize: 'none',
            fontFamily: props.mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : FORM_UI_FONT,
            fontSize: 12,
            color: C.ink,
            background: C.bg,
            border: `1px solid ${C.line}`,
            borderRadius: 8,
            padding: '9px 11px',
            lineHeight: 1.5,
            whiteSpace: props.multiline ? 'pre' : 'nowrap',
            overflowX: 'auto',
          }}
        />
        <button
          type="button"
          onClick={copy}
          style={{
            flexShrink: 0,
            alignSelf: props.multiline ? 'flex-start' : 'stretch',
            fontFamily: FORM_UI_FONT,
            fontSize: 12,
            fontWeight: 700,
            color: copied ? C.tealDark : C.indigo,
            background: copied ? C.contactBg : '#fff',
            border: `1px solid ${copied ? C.tealDark : C.line}`,
            borderRadius: 8,
            padding: '0 14px',
            height: props.multiline ? 34 : 'auto',
            cursor: 'pointer',
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function ChecklistRow(props: { label: string; pass: boolean; isLast: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '10px 0',
        borderBottom: props.isLast ? 'none' : `1px solid ${C.line}`,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 99,
          flexShrink: 0,
          background: props.pass ? C.contactBg : C.dangerBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon
          d={props.pass ? ICONS.check : ICONS.x}
          size={12}
          color={props.pass ? C.tealDark : C.danger}
          sw={2.6}
        />
      </span>
      <span
        style={{
          fontSize: 13,
          color: props.pass ? C.ink : C.danger,
          fontWeight: props.pass ? 500 : 600,
          flex: 1,
        }}
      >
        {props.label}
      </span>
      {!props.pass && (
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.indigo,
            cursor: 'pointer',
          }}
        >
          Fix →
        </span>
      )}
    </div>
  );
}
